/**
 * Shared session display/filter configs.
 */
import type { ManagedByValue } from '@/components/common/ManagedByMultiSelect';
import {
  BookMarked,
  User,
  Users,
  CheckCircle2,
  Circle,
  PlayCircle,
  XCircle,
  Clock4,
  GraduationCap,
  Info,
  MapPin,
  AlertTriangle,
  UserX,
  Video,
  Wifi,
  AlertCircle,
  CalendarOff,
  CalendarDays,
  Repeat,
  CalendarRange,
  Repeat2,
  CalendarCheck,
  SlidersHorizontal,
  CalendarClock,
  CalendarPlus,
  CloudOff,
  MessageSquare,
  Handshake,
  FileText,
  Star,
  History,
  Heart,
  Scale,
} from 'lucide-react';
import type { LucideIcon } from 'lucide-react';
import type { BadgeVariant } from '@/components/common/LabelBadge';
export {
  DATE_RANGE_PERIOD_OPTIONS_FULL,
  DATE_RANGE_PERIOD_OPTIONS_WITH_TODAY,
  PAIR_TYPE_CONFIG,
  SESSION_REMOTE_TYPE_CONFIG,
  SESSION_TYPE_CONFIG,
  SESSION_TYPE_FILTER_KEYS,
  STUDENT_ABSENCE_REASONS,
  TUTOR_ABSENCE_REASONS,
} from '@/constants/shared-catalog';
import { getRoleLabel, getRoleLabelPlural } from '@/utils/role-label-utils';
import type {
  SessionStatus,
  SessionTab as SessionInPersonTab,
  PlanificationType,
  CancelSessionReasonCode,
  SessionGroupStatus,
  SessionType,
} from '@/types/inperson/session.types';
import type {
  CancellationReasonCode,
  LiveKitRoomPresenceStatus,
  SessionTab as SessionRemoteTab,
} from '@/types/remote/session.types';

/** Badge + icon config for a session status. */
export interface SessionStatusConfig {
  label: string;
  icon: LucideIcon;
  badgeVariant: BadgeVariant;
  badgeColor: string;
  /** Solid fill when the status filter is active (IN_PROGRESS). */
  filterActiveVariant?: 'solid';
  /** Blinking red dot for the in-progress status. */
  isInProgress?: boolean;
}

/** Badge + icon config for a participant attendance status. */
export interface AttendanceStatusConfig {
  label: string;
  icon: LucideIcon;
  badgeVariant: BadgeVariant;
  badgeColor: string;
}

/** Tooltip on whole-class type badge (one report for the session). */
export const WHOLE_CLASS_REPORT_HINT = '1 compte rendu généré pour cette séance';

/** Recurrence type labels (API, matching, forms). */
export const SESSION_RECURRENCE_LABELS: Record<string, string> = {
  none: 'Aucune (séance unique)',
  weekly: 'Hebdomadaire',
  ab: 'Bi-mensuelle (A/B)',
};

/** Badge + icon config for a session recurrence type. */
export interface RecurrenceConfig {
  label: string;
  icon: LucideIcon;
  badgeVariant: BadgeVariant;
  badgeColor: string;
}

/** Recurrence badge config (label + icon + color). */
export const SESSION_RECURRENCE_CONFIG: Record<string, RecurrenceConfig> = {
  none: {
    label: 'Aucune',
    icon: CalendarOff,
    badgeVariant: 'ghost',
    badgeColor: 'rgb(var(--color-muted, 128 128 128))',
  },
  once: {
    label: 'Séance unique',
    icon: CalendarOff,
    badgeVariant: 'ghost',
    badgeColor: 'rgb(var(--color-muted, 128 128 128))',
  },
  daily: {
    label: 'Quotidienne',
    icon: CalendarDays,
    badgeVariant: 'outlined',
    badgeColor: 'var(--color-2-nuance-1)',
  },
  weekly: {
    label: 'Hebdomadaire',
    icon: Repeat,
    badgeVariant: 'outlined',
    badgeColor: 'var(--color-1-nuance-1)',
  },
  several_days_per_week: {
    label: 'Plusieurs jours/sem.',
    icon: CalendarRange,
    badgeVariant: 'outlined',
    badgeColor: 'var(--color-1-nuance-2)',
  },
  biweekly: {
    label: 'Toutes les 2 semaines',
    icon: Repeat2,
    badgeVariant: 'outlined',
    badgeColor: 'var(--color-2-nuance-2)',
  },
  monthly: {
    label: 'Mensuelle',
    icon: CalendarCheck,
    badgeVariant: 'outlined',
    badgeColor: 'var(--color-3-nuance-1)',
  },
  custom: {
    label: 'Personnalisée',
    icon: SlidersHorizontal,
    badgeVariant: 'outlined',
    badgeColor: 'var(--color-3-nuance-2)',
  },
  ab: {
    label: 'Bi-mensuelle A/B',
    icon: CalendarClock,
    badgeVariant: 'outlined',
    badgeColor: 'rgb(var(--accent-purple))',
  },
  yearly: {
    label: 'Annuelle',
    icon: CalendarRange,
    badgeVariant: 'outlined',
    badgeColor: 'var(--color-4-nuance-1)',
  },
};

/** LabelBadge for a remote pair one-off session (list + details). */
export const PAIR_ONE_OFF_BADGE_CONFIG = {
  label: 'Ponctuelle',
  icon: CalendarPlus,
  badgeVariant: 'ghost' as BadgeVariant,
  badgeColor: 'var(--color-6-nuance-1)',
};

/** Badge config for each in-person session status. */
export const SESSION_STATUS_CONFIG: Record<SessionStatus, SessionStatusConfig> = {
  PLANNED: {
    label: 'Planifiée',
    icon: Clock4,
    badgeVariant: 'outlined',
    badgeColor: 'var(--color-2-nuance-1)',
  },
  IN_PROGRESS: {
    label: 'En cours',
    icon: Circle,
    badgeVariant: 'outlined',
    badgeColor: 'rgb(var(--color-error))',
    filterActiveVariant: 'solid',
    isInProgress: true,
  },
  COMPLETED: {
    label: 'Terminée',
    icon: CheckCircle2,
    badgeVariant: 'outlined',
    badgeColor: 'rgb(var(--status-success))',
  },
  CLOSED: {
    label: 'Clôturée',
    icon: CheckCircle2,
    badgeVariant: 'outlined',
    badgeColor: 'rgb(var(--accent-purple))',
  },
  CANCELLED: {
    label: 'Annulée',
    icon: XCircle,
    badgeVariant: 'outlined',
    badgeColor: 'rgb(var(--color-error))',
  },
  // Remote-only statuses — for type completeness; use SESSION_REMOTE_STATUS_CONFIG for remote sessions
  TO_LAUNCH: {
    label: 'À lancer',
    icon: PlayCircle,
    badgeVariant: 'outlined',
    badgeColor: 'var(--color-2-nuance-2)',
  },
  STUDENT_ABSENT: {
    get label() {
      return `${getRoleLabel('student')} absent`;
    },
    icon: UserX,
    badgeVariant: 'outlined',
    badgeColor: 'rgb(var(--status-warning))',
  },
  TUTOR_ABSENT: {
    get label() {
      return `${getRoleLabel('tutor')} absent`;
    },
    icon: AlertTriangle,
    badgeVariant: 'outlined',
    badgeColor: 'rgb(var(--status-warning))',
  },
  BOTH_ABSENT: {
    label: 'Binôme absent',
    icon: Users,
    badgeVariant: 'outlined',
    badgeColor: 'rgb(var(--status-warning))',
  },
  NOT_COMPLETED: {
    label: 'Non réalisée',
    icon: XCircle,
    badgeVariant: 'outlined',
    badgeColor: 'rgb(var(--status-warning))',
  },
  PARTICIPANTS_ABSENT: {
    label: 'Non réalisée',
    icon: XCircle,
    badgeVariant: 'outlined',
    badgeColor: 'rgb(var(--status-warning))',
  },
};

/** Statuses shared by in-person tutoring session types. */
/** Tutoring statuses reused for every in-person tutoring type. */
export const SESSION_IN_PERSON_TUTORING_STATUS_KEYS: SessionStatus[] = [
  'PLANNED',
  'IN_PROGRESS',
  'COMPLETED',
  'CLOSED',
  'CANCELLED',
];

/** Filterable statuses by type (mirrors remote {@link SESSION_REMOTE_STATUS_KEYS_BY_TYPE}). */
export const SESSION_IN_PERSON_STATUS_KEYS_BY_TYPE: Record<string, SessionStatus[]> = {
  information_meeting_in_person: [
    'PLANNED',
    'IN_PROGRESS',
    'COMPLETED',
    'NOT_COMPLETED',
    'CANCELLED',
  ],
  homework_help: SESSION_IN_PERSON_TUTORING_STATUS_KEYS,
  individual_tutoring: SESSION_IN_PERSON_TUTORING_STATUS_KEYS,
  workshop: SESSION_IN_PERSON_TUTORING_STATUS_KEYS,
  whole_class: SESSION_IN_PERSON_TUTORING_STATUS_KEYS,
  group_tutoring: SESSION_IN_PERSON_TUTORING_STATUS_KEYS,
  quick_help_desk: SESSION_IN_PERSON_TUTORING_STATUS_KEYS,
};

/** Display order for in-person session status chips. */
export const IN_PERSON_STATUS_DISPLAY_ORDER: SessionStatus[] = [
  'PLANNED',
  'IN_PROGRESS',
  'COMPLETED',
  'CLOSED',
  'NOT_COMPLETED',
  'CANCELLED',
];

/** Group-level statuses (CLOSED is session-only). */
export const SESSION_GROUP_STATUS_CONFIG: Record<SessionGroupStatus, SessionStatusConfig> = {
  PLANNED: {
    label: 'Planifiée',
    icon: Clock4,
    badgeVariant: 'outlined',
    badgeColor: 'var(--color-2-nuance-1)',
  },
  IN_PROGRESS: {
    label: 'En cours',
    icon: Circle,
    badgeVariant: 'outlined',
    badgeColor: 'rgb(var(--color-error))',
    filterActiveVariant: 'solid',
    isInProgress: true,
  },
  COMPLETED: {
    label: 'Terminée',
    icon: CheckCircle2,
    badgeVariant: 'outlined',
    badgeColor: 'rgb(var(--status-success))',
  },
  CANCELLED: {
    label: 'Annulée',
    icon: XCircle,
    badgeVariant: 'outlined',
    badgeColor: 'rgb(var(--color-error))',
  },
};

/** Active filter state for the session list. */
export interface SessionFilters {
  types: string[];
  statuses: string[];
  /** Predefined period shortcut. Set to 'all' when customDateFrom/customDateTo are used. */
  dateRange: 'all' | 'today' | 'week' | 'month';
  /** Custom date range — mutually exclusive with dateRange != 'all'. */
  customDateFrom?: Date;
  customDateTo?: Date;
  establishmentId?: string;
  subjectId?: string;
  levelId?: string;
  search?: string;
  recurrenceType?: string;
  /** Filter sessions by a specific tutor participant (user id). */
  tutorId?: string;
  /** Filter sessions by a specific animator participant (coordinator/recruiter user id). */
  animatorUserId?: string;
}

/** Default in-person session list filters. */
export const DEFAULT_SESSION_FILTERS: SessionFilters = {
  types: [],
  statuses: [],
  dateRange: 'month',
  customDateFrom: undefined,
  customDateTo: undefined,
  establishmentId: undefined,
  subjectId: undefined,
  levelId: undefined,
  search: undefined,
  recurrenceType: undefined,
  tutorId: undefined,
  animatorUserId: undefined,
};

/** Filter group IDs matching `interface_elements.element_id` for sessions. */
export type FilterGroupId =
  | 'filter-period'
  | 'filter-date-range'
  | 'filter-type'
  | 'filter-status'
  | 'filter-establishment'
  | 'filter-subject'
  | 'filter-level'
  | 'filter-search'
  | 'filter-recurrence'
  | 'filter-animator';

/** All in-person session filter group ids (configure-filters panel). */
export const ALL_FILTER_GROUP_IDS: FilterGroupId[] = [
  'filter-period',
  'filter-date-range',
  'filter-type',
  'filter-status',
  'filter-establishment',
  'filter-subject',
  'filter-level',
  'filter-search',
  'filter-recurrence',
  'filter-animator',
];

/** Human-readable label per filter group, for the configure panel. */
export const FILTER_GROUP_LABELS: Record<FilterGroupId, string> = {
  'filter-period': 'Période',
  'filter-date-range': 'Date personnalisée',
  'filter-type': 'Type',
  'filter-status': 'Statut',
  'filter-establishment': 'Établissement',
  'filter-subject': 'Matière',
  'filter-level': 'Niveau',
  'filter-search': 'Recherche participant',
  'filter-recurrence': 'Récurrence',
  'filter-animator': 'Animateur',
};

/** Sequential tab colors — shared between dashboard section and management page. */
export const SESSION_REMOTE_TAB_COLORS: Record<SessionRemoteTab, string> = {
  all: 'var(--color-5-nuance-1)',
  tutoring: 'var(--color-1-nuance-1)',
  'information-meeting': 'var(--color-2-nuance-1)',
  'welcome-meeting': 'var(--color-3-nuance-1)',
  'field-event': 'rgb(var(--accent-purple))',
};

/** Create-flow card for a remote session type. */
export interface SessionRemoteCreateTypeOption {
  id: SessionRemoteTab;
  title: string;
  description: string;
  icon: LucideIcon;
  accentVar: string;
}

/** Create-flow cards for remote session types. */
export const SESSION_REMOTE_CREATE_TYPE_OPTIONS: SessionRemoteCreateTypeOption[] = [
  {
    id: 'tutoring',
    title: 'Tutorat',
    description: 'Séance de tutorat individuel en visio.',
    icon: User,
    accentVar: SESSION_REMOTE_TAB_COLORS.tutoring,
  },
  {
    id: 'information-meeting',
    title: "Réunion d'information",
    description:
      "Réunion d'information avec animateur(s) et jusqu'à 25 tuteurs participants en visio.",
    icon: Info,
    accentVar: SESSION_REMOTE_TAB_COLORS['information-meeting'],
  },
  {
    id: 'welcome-meeting',
    title: "Réunion d'accueil",
    description: "Réunion d'accueil avec animateur(s), parent et élève en visio.",
    icon: Users,
    accentVar: SESSION_REMOTE_TAB_COLORS['welcome-meeting'],
  },
  {
    id: 'field-event',
    title: 'Événement terrain',
    description: 'Événement de recrutement en présentiel (amphi, forum, entreprise, etc.).',
    icon: MapPin,
    accentVar: SESSION_REMOTE_TAB_COLORS['field-event'],
  },
];

/** Create-type options visible for the allowed remote tabs (all when unrestricted). */
export const getVisibleRemoteCreateTypeOptions = (
  allowedTabIds?: SessionRemoteTab[],
): SessionRemoteCreateTypeOption[] => {
  if (allowedTabIds && allowedTabIds.length > 0) {
    return SESSION_REMOTE_CREATE_TYPE_OPTIONS.filter((opt) => allowedTabIds.includes(opt.id));
  }
  return SESSION_REMOTE_CREATE_TYPE_OPTIONS;
};

/** Remote session type badges (icons, colors). */
/** Remote types whose backend computes `facilitators` directly. */
export const REMOTE_FACILITATOR_SESSION_TYPES = new Set<string>([
  'information_meeting',
  'welcome_meeting',
  'workshop',
]);

/** Remote types that have an animator (backend-computed or derived from participants). */
export const REMOTE_ANIMATOR_SESSION_TYPES = new Set<string>([
  'information_meeting',
  'welcome_meeting',
  'whole_class',
  'workshop',
]);

/** Remote session status badges (all 12 statuses). */
export const SESSION_REMOTE_STATUS_CONFIG: Record<SessionStatus, SessionStatusConfig> = {
  PLANNED: {
    label: 'À venir',
    icon: Clock4,
    badgeVariant: 'outlined',
    badgeColor: 'var(--color-1-nuance-2)',
  },
  TO_LAUNCH: {
    label: 'À lancer',
    icon: PlayCircle,
    badgeVariant: 'outlined',
    badgeColor: 'var(--color-2-nuance-3)',
    isInProgress: false,
  },
  IN_PROGRESS: {
    label: 'En cours',
    icon: Circle,
    badgeVariant: 'outlined',
    badgeColor: 'rgb(var(--color-error))',
    filterActiveVariant: 'solid',
    isInProgress: true, // Blinking red
  },
  COMPLETED: {
    label: 'Réalisée',
    icon: CheckCircle2,
    badgeVariant: 'outlined',
    badgeColor: 'rgb(var(--status-success))',
  },
  CLOSED: {
    label: 'Clôturée',
    icon: CheckCircle2,
    badgeVariant: 'outlined',
    badgeColor: 'rgb(var(--accent-purple))',
  },
  CANCELLED: {
    label: 'Annulée',
    icon: XCircle,
    badgeVariant: 'outlined',
    badgeColor: 'rgb(var(--color-error))',
  },
  STUDENT_ABSENT: {
    get label() {
      return `${getRoleLabel('student')} absent`;
    },
    icon: UserX,
    badgeVariant: 'outlined',
    badgeColor: 'var(--color-4-nuance-2)',
  },
  TUTOR_ABSENT: {
    get label() {
      return `${getRoleLabel('tutor')} absent`;
    },
    icon: AlertTriangle,
    badgeVariant: 'outlined',
    badgeColor: 'var(--color-5-nuance-3)',
  },
  BOTH_ABSENT: {
    label: 'Binôme absent',
    icon: Users,
    badgeVariant: 'outlined',
    badgeColor: 'var(--color-5-nuance-2)',
  },
  NOT_COMPLETED: {
    label: 'Non réalisée',
    icon: XCircle,
    badgeVariant: 'outlined',
    badgeColor: 'rgb(var(--status-warning))',
  },
  PARTICIPANTS_ABSENT: {
    label: 'Non réalisée',
    icon: XCircle,
    badgeVariant: 'outlined',
    badgeColor: 'rgb(var(--status-warning))',
  },
};

/**
 * Message shown in {@link SessionReportsTab} when no report exists yet for a given session type.
 */
export const SESSION_REMOTE_REPORT_EMPTY_MESSAGE: Record<string, string> = {
  individual_tutoring: 'Pas encore de compte rendu de tutorat ni de scoring',
  group_tutoring: 'Pas encore de compte rendu de tutorat ni de scoring',
  information_meeting: "Pas encore de compte rendu de réunion d'information",
  welcome_meeting: "Pas encore de compte rendu de réunion d'accueil",
  workshop: 'Pas encore de compte rendu de séance terrain',
};

/**
 * Why the report/scoring is not yet available ({@link SessionReportsTab} empty state).
 */
export const SESSION_REMOTE_REPORT_AVAILABILITY_MESSAGE: Partial<Record<SessionStatus, string>> = {
  PLANNED: 'Compte rendu et scoring disponibles une fois la séance terminée',
  TO_LAUNCH: 'Compte rendu et scoring disponibles une fois la séance terminée',
  IN_PROGRESS: 'La séance est en cours — le compte rendu sera disponible à la clôture',
  COMPLETED: 'En attente de saisie par le coordinateur',
  CLOSED: 'En attente de saisie par le coordinateur',
};

/** Types in the session-type filter (All + Tutoring tabs). */
export const SESSION_REMOTE_TYPE_FILTER_KEYS = [
  'individual_tutoring',
  'information_meeting',
  'welcome_meeting',
  'workshop',
] as const;

/** Filterable statuses by session type. */
export const SESSION_REMOTE_STATUS_KEYS_BY_TYPE: Record<string, SessionStatus[]> = {
  individual_tutoring: [
    'PLANNED',
    'TO_LAUNCH',
    'IN_PROGRESS',
    'COMPLETED',
    'STUDENT_ABSENT',
    'TUTOR_ABSENT',
    'BOTH_ABSENT',
    'CANCELLED',
  ],
  information_meeting: [
    'PLANNED',
    'TO_LAUNCH',
    'IN_PROGRESS',
    'COMPLETED',
    'NOT_COMPLETED',
    'CANCELLED',
  ],
  welcome_meeting: [
    'PLANNED',
    'TO_LAUNCH',
    'IN_PROGRESS',
    'COMPLETED',
    'NOT_COMPLETED',
    'CANCELLED',
  ],
  workshop: ['PLANNED', 'TO_LAUNCH', 'IN_PROGRESS', 'COMPLETED', 'CANCELLED'],
};

/** Display order for remote session status chips. */
export const REMOTE_STATUS_DISPLAY_ORDER: SessionStatus[] = [
  'PLANNED',
  'TO_LAUNCH',
  'IN_PROGRESS',
  'COMPLETED',
  'STUDENT_ABSENT',
  'TUTOR_ABSENT',
  'BOTH_ABSENT',
  'NOT_COMPLETED',
  'CANCELLED',
];

/** Statuses that cannot be modified once reached. */
export const TERMINAL_SESSION_STATUSES: SessionStatus[] = [
  'COMPLETED',
  'CLOSED',
  'CANCELLED',
  'STUDENT_ABSENT',
  'TUTOR_ABSENT',
  'BOTH_ABSENT',
  'NOT_COMPLETED',
  'PARTICIPANTS_ABSENT',
];

/** Badge + icon config for a LiveKit room presence status. */
export interface LiveKitRoomStatusConfig {
  label: string;
  icon: LucideIcon;
  badgeVariant: BadgeVariant;
  badgeColor: string;
}

/** Badge config for each LiveKit room presence status. */
export const LIVEKIT_ROOM_STATUS_CONFIG: Record<
  LiveKitRoomPresenceStatus,
  LiveKitRoomStatusConfig
> = {
  preparation: {
    label: 'En préparation',
    icon: Clock4,
    badgeVariant: 'outlined',
    badgeColor: 'var(--color-2-nuance-1)',
  },
  open: {
    label: 'Salle ouverte',
    icon: Wifi,
    badgeVariant: 'outlined',
    badgeColor: 'var(--color-1-nuance-1)',
  },
  in_progress: {
    label: 'En cours',
    icon: Circle,
    badgeVariant: 'outlined',
    badgeColor: 'rgb(var(--color-error))',
  },
  finished: {
    label: 'Terminée',
    icon: CheckCircle2,
    badgeVariant: 'outlined',
    badgeColor: 'rgb(var(--status-success))',
  },
  error: {
    label: 'Erreur',
    icon: AlertCircle,
    badgeVariant: 'outlined',
    badgeColor: 'rgb(var(--color-error))',
  },
};

/** LiveKit businessRole "animator" badge. */
export const LIVEKIT_BUSINESS_ROLE_ANIMATOR_CONFIG = {
  label: 'Animateur',
  icon: Video,
  badgeVariant: 'ghost' as BadgeVariant,
  badgeColor: 'var(--color-3-nuance-1)',
};

/** Program badge for session detail views (programName/programId). */
export const PROGRAM_BADGE_CONFIG = {
  label: 'Programme',
  icon: BookMarked,
  badgeVariant: 'ghost' as BadgeVariant,
  badgeColor: 'var(--color-2-nuance-1)',
};

/** Attendance badges for remote session participant tabs. */
export const ATTENDANCE_STATUS_CONFIG: Record<string, AttendanceStatusConfig> = {
  present: {
    label: 'Présent',
    icon: CheckCircle2,
    badgeVariant: 'outlined',
    badgeColor: 'rgb(var(--status-success))',
  },
  absent: {
    label: 'Absent',
    icon: XCircle,
    badgeVariant: 'outlined',
    badgeColor: 'rgb(var(--color-error))',
  },
  excused: {
    label: 'Absent excusé',
    icon: AlertTriangle,
    badgeVariant: 'outlined',
    badgeColor: 'rgb(var(--status-warning))',
  },
  pending: {
    label: 'En attente',
    icon: Clock4,
    badgeVariant: 'outlined',
    badgeColor: 'rgb(var(--color-1-nuance-3))',
  },
};

/** Remote session list filters. */
export interface SessionRemoteFilters {
  types: string[];
  statuses: SessionStatus[];
  dateRange: 'all' | 'today' | 'week' | 'month' | 'year';
  customDateFrom?: Date;
  customDateTo?: Date;
  search?: string;
  justificationFilter?: 'justified' | 'unjustified' | 'all';
  tutorUserId?: string;
  studentUserId?: string;
  managedByIds?: ManagedByValue;
  animatorUserId?: string;
  programId?: string;
  schoolYearId?: string;
  recurrenceType?: string;
}

/** Defaults aligned with the remote session KPI cards (whole tutoring family, all time). */
export const DEFAULT_SESSION_REMOTE_FILTERS: SessionRemoteFilters = {
  types: ['individual_tutoring', 'homework_help', 'group_tutoring'],
  statuses: [],
  dateRange: 'all',
  search: undefined,
  justificationFilter: 'all',
  recurrenceType: undefined,
};

/** Remote filter group IDs (match seeder interface_elements). */
export type SessionRemoteFilterGroupId =
  | 'filter-period'
  | 'filter-date-range'
  | 'filter-status'
  | 'filter-search'
  | 'filter-tutor'
  | 'filter-student'
  | 'filter-managed-by'
  | 'filter-program'
  | 'filter-school-year'
  | 'filter-recurrence-information-meeting'
  | 'filter-recurrence-welcome-meeting';

/** All remote session filter group ids (configure-filters panel). */
export const ALL_SESSION_REMOTE_FILTER_GROUP_IDS: readonly SessionRemoteFilterGroupId[] = [
  'filter-period',
  'filter-date-range',
  'filter-status',
  'filter-search',
  'filter-tutor',
  'filter-student',
  'filter-managed-by',
  'filter-program',
  'filter-school-year',
  'filter-recurrence-information-meeting',
  'filter-recurrence-welcome-meeting',
] as const;

/** Human-readable label per remote session filter group. */
export const SESSION_REMOTE_FILTER_GROUP_LABELS: Record<SessionRemoteFilterGroupId, string> = {
  'filter-period': 'Période',
  'filter-date-range': 'Date personnalisée',
  'filter-status': 'Statut',
  'filter-search': 'Recherche',
  get 'filter-tutor'() {
    return getRoleLabel('tutor');
  },
  get 'filter-student'() {
    return getRoleLabel('student');
  },
  'filter-managed-by': 'Géré par',
  'filter-program': 'Programme',
  'filter-school-year': 'Année scolaire',
  'filter-recurrence-information-meeting': "Récurrence (Réunion d'information)",
  'filter-recurrence-welcome-meeting': "Récurrence (Réunion d'accueil)",
};

/** Fallback participant role labels when LiveKit presence roleLabel is null. */
export const SESSION_PARTICIPANT_ROLE_LABELS: Record<string, string> = new Proxy(
  {} as Record<string, string>,
  {
    get: (_target, code) => (typeof code === 'string' ? getRoleLabel(code) : undefined),
  },
);

/** Selectable cancellation reason (code, label, comment requirement). */
export interface CancellationReason {
  code: CancellationReasonCode;
  label: string;
  requiresComment: boolean;
}

/** Remote session cancellation reason options. */
export const CANCELLATION_REASONS: CancellationReason[] = [
  { code: 'UNAVAILABILITY', label: 'Indisponibilité exceptionnelle', requiresComment: false },
  { code: 'SCHEDULE_CONFLICT', label: "Conflit d'emploi du temps", requiresComment: false },
  { code: 'STUDENT_REQUEST', label: "Demande de l'élève", requiresComment: false },
  { code: 'MUTUAL_AGREEMENT', label: 'Accord commun', requiresComment: false },
  { code: 'OTHER', label: 'Autre', requiresComment: true },
];

/** Cancellation reasons as {@link Select} options. */
export const CANCELLATION_REASON_SELECT_OPTIONS = CANCELLATION_REASONS.map((reason) => ({
  value: reason.code,
  label: reason.label,
}));

/** Copy for the remote session create-type picker. */
export const SESSION_REMOTE_SELECT_TYPE = {
  title: 'Créer une séance',
  subtitle: 'Sélectionnez le type de séance à créer.',
} as const;

/** In-person session cancellation reason options. */
export const SESSION_CANCEL_REASON_OPTIONS: Array<{
  value: CancelSessionReasonCode;
  label: string;
}> = [
  { value: 'STUDENT_ABSENCE', label: "Absence d'un élève" },
  { value: 'TUTOR_ABSENCE', label: "Absence d'un tuteur" },
  { value: 'VSC_ABSENCE', label: "Absence d'un VSC" },
  { value: 'WEATHER', label: 'Conditions météo' },
  { value: 'ROOM_UNAVAILABLE', label: 'Salle indisponible' },
  { value: 'OTHER', label: 'Autre raison' },
];

/**
 * Unified cancellation reason config — covers both web-remote keys and mobile keys.
 * Used to render {@link LabelBadge} with icon + color in session detail views.
 * For OTHER: caller should display reasonDetail/comment text instead of the label.
 */
export interface CancellationReasonConfig {
  label: string;
  icon: LucideIcon;
  badgeColor: string;
  badgeVariant: 'ghost' | 'outlined' | 'solid';
  requiresDetail: boolean;
}

/** Badge config for each cancellation reason (web-remote + mobile keys). */
export const CANCELLATION_REASON_CONFIG: Record<string, CancellationReasonConfig> = {
  UNAVAILABILITY: {
    label: 'Indisponibilité exceptionnelle',
    icon: CalendarOff,
    badgeColor: 'rgb(var(--status-error))',
    badgeVariant: 'ghost',
    requiresDetail: false,
  },
  SCHEDULE_CONFLICT: {
    label: "Conflit d'emploi du temps",
    icon: CalendarOff,
    badgeColor: 'rgb(var(--status-warning))',
    badgeVariant: 'ghost',
    requiresDetail: false,
  },
  STUDENT_REQUEST: {
    label: "Demande de l'élève",
    icon: GraduationCap,
    badgeColor: 'var(--color-2-nuance-1)',
    badgeVariant: 'ghost',
    requiresDetail: false,
  },
  MUTUAL_AGREEMENT: {
    label: 'Accord commun',
    icon: Handshake,
    badgeColor: 'var(--color-1-nuance-1)',
    badgeVariant: 'ghost',
    requiresDetail: false,
  },
  STUDENT_ABSENCE: {
    label: 'Absence élève',
    icon: GraduationCap,
    badgeColor: 'rgb(var(--status-error))',
    badgeVariant: 'ghost',
    requiresDetail: false,
  },
  TUTOR_ABSENCE: {
    label: 'Indisponibilité tuteur',
    icon: UserX,
    badgeColor: 'rgb(var(--status-error))',
    badgeVariant: 'ghost',
    requiresDetail: false,
  },
  VSC_ABSENCE: {
    label: 'Absence VSC',
    icon: UserX,
    badgeColor: 'rgb(var(--status-warning))',
    badgeVariant: 'ghost',
    requiresDetail: false,
  },
  WEATHER: {
    label: 'Conditions météo',
    icon: CloudOff,
    badgeColor: 'var(--color-2-nuance-1)',
    badgeVariant: 'ghost',
    requiresDetail: false,
  },
  ROOM_UNAVAILABLE: {
    label: 'Salle indisponible',
    icon: MapPin,
    badgeColor: 'rgb(var(--status-warning))',
    badgeVariant: 'ghost',
    requiresDetail: false,
  },
  OTHER: {
    label: 'Autre',
    icon: MessageSquare,
    badgeColor: 'var(--color-1-nuance-2)',
    badgeVariant: 'ghost',
    requiresDetail: true,
  },
};

/** Field event types. */
export type FieldEventType =
  | 'amphi-etudiant'
  | 'forum-associatif'
  | 'entreprise'
  | 'networking'
  | 'webinar'
  | 'festival';

/** Display labels for field event types. */
export const FIELD_EVENT_TYPE_LABELS: Record<FieldEventType, string> = {
  'amphi-etudiant': 'Amphi étudiant',
  'forum-associatif': 'Forum associatif',
  entreprise: 'Visite entreprise',
  networking: 'Networking',
  webinar: 'Webinaire',
  festival: 'Festival',
};

/** Budget post types for field events. */
export type BudgetPostType = 'venue-booking' | 'communication' | 'transport' | 'other';

/** Display labels for field-event budget posts. */
export const BUDGET_POST_TYPE_LABELS: Record<BudgetPostType, string> = {
  'venue-booking': 'Réservation salle / stand',
  communication: 'Communication',
  transport: 'Transport',
  other: 'Autres',
};

/** Sequential tab colors for in-person session tabs. */
export const SESSION_IN_PERSON_TAB_COLORS: Record<SessionInPersonTab, string> = {
  tutoring: 'var(--color-1-nuance-1)',
  'information-meeting': 'var(--color-2-nuance-1)',
};

/** Search placeholders per in-person session tab. */
export const SESSION_IN_PERSON_SEARCH_PLACEHOLDERS: Record<'all' | SessionInPersonTab, string> = {
  all: 'Recherche globale...',
  tutoring: 'Nom tuteur ou élève...',
  'information-meeting': 'Animateur ou participant tuteur...',
};

/**
 * Session statuses for which joining a remote meeting is allowed.
 * Single source of truth — update here if new joinable statuses are added.
 */
export const JOINABLE_SESSION_STATUSES = new Set<string>(['IN_PROGRESS', 'TO_LAUNCH']);

/** Statuses for which a report can be written/read. */
export const REPORT_ELIGIBLE_STATUSES = new Set<string>([
  'COMPLETED',
  'CLOSED',
  'STUDENT_ABSENT',
  'TUTOR_ABSENT',
  'BOTH_ABSENT',
]);

/** Report statuses considered fully validated (read-only view). */
export const VALIDATED_REPORT_STATUSES = new Set<string>(['valide', 'complet']);

/** Search placeholders per remote session tab. */
export const SESSION_REMOTE_SEARCH_PLACEHOLDERS: Record<SessionRemoteTab, string> = {
  tutoring: 'Nom tuteur ou élève...',
  'information-meeting': 'Animateur ou participant tuteur...',
  'welcome-meeting': 'Animateur, élève ou parent...',
  'field-event': 'Animateur ou recruteur...',
  all: 'Recherche globale...',
};

/** Max participants for a remote information meeting. */
export const INFORMATION_MEETING_MAX_PARTICIPANTS = 25;

/** Default values for the remote session create form. */
export const SESSION_REMOTE_FORM_DEFAULTS = {
  maxParticipants: INFORMATION_MEETING_MAX_PARTICIPANTS,
  etpUnitCost: 150,
  defaultBudgetPostType: 'venue_booking',
} as const;

/** Role codes used when picking remote session animators. */
export const SESSION_REMOTE_FORM_ROLES = {
  coordinator: 'coordinator_remote',
  recruiter: 'recruiter_remote',
} as const;

/** React Query keys for remote session form lookups. */
export const SESSION_REMOTE_FORM_QUERY_KEYS = {
  tutoringTutors: (search: string) =>
    ['tutoring-tutors', search, 'active', 'awaiting_student', 'tutoring_in_progress'] as const,
  tutoringStudents: (search: string) =>
    ['tutoring-students', search, 'awaiting_tutor', 'active'] as const,
  tutorParticipants: (search: string) => ['tutor-participants', search] as const,
  studentParticipants: (search: string, sessionType: string) =>
    ['student-participants', search, sessionType] as const,
  recurrencePreview: (type: string, sessionDate: string, recurrenceType: string, ruleKey: string) =>
    ['recurrence-preview', type, sessionDate, recurrenceType, ruleKey] as const,
};

/** Recurrence {@link Select} options for the remote session form. */
export const SESSION_REMOTE_RECURRENCE_SELECT_OPTIONS = [
  { value: '', label: '-- Sélectionnez --' },
  ...Object.entries(SESSION_RECURRENCE_LABELS).map(([value, label]) => ({ value, label })),
];

/** Recurrence {@link Select} options for remote meeting create forms (RI/RA). */
export const SESSION_REMOTE_MEETING_RECURRENCE_SELECT_OPTIONS = [
  { value: '', label: '-- Sélectionnez --' },
  { value: 'none', label: 'Aucune (séance unique)' },
  { value: 'daily', label: 'Quotidienne' },
  { value: 'weekly', label: 'Hebdomadaire' },
  { value: 'biweekly', label: 'Toutes les 2 semaines' },
  { value: 'monthly', label: 'Mensuelle' },
  { value: 'yearly', label: 'Annuelle' },
  { value: 'custom', label: 'Dates personnalisées' },
];

/** Recurrence filter {@link Select} options for remote meetings (RI/RA list filters). */
export const SESSION_REMOTE_MEETING_RECURRENCE_FILTER_OPTIONS = [
  { value: '', label: 'Toutes' },
  { value: 'none', label: 'Aucune (séance unique)' },
  { value: 'daily', label: 'Quotidienne' },
  { value: 'weekly', label: 'Hebdomadaire' },
  { value: 'biweekly', label: 'Toutes les 2 semaines' },
  { value: 'monthly', label: 'Mensuelle' },
  { value: 'custom', label: 'Personnalisée' },
];

/** Field-event type {@link Select} options. */
export const FIELD_EVENT_TYPE_SELECT_OPTIONS = [
  { value: '', label: '-- Sélectionnez --' },
  ...Object.entries(FIELD_EVENT_TYPE_LABELS).map(([value, label]) => ({ value, label })),
];

/** Budget-post type {@link Select} options. */
export const BUDGET_POST_SELECT_OPTIONS = Object.entries(BUDGET_POST_TYPE_LABELS).map(
  ([value, label]) => ({ value, label }),
);

/** Role codes allowed as information-meeting animators. */
export const INFORMATION_MEETING_ANIMATOR_ROLE_CODES = [
  'recruiter',
  'coordinator',
  'manager',
] as const;

/** Role codes allowed as information-meeting participants. */
export const INFORMATION_MEETING_PARTICIPANT_ROLE_CODES = ['tutor', 'volunteer'] as const;

/** Recurrence × groups count that triggers the heavy-create warning. */
export const HEAVY_CREATE_THRESHOLD = 100;

/** Droppable id for the empty matching zone. */
export const NEW_SESSION_DROP_ID = 'new-session';

/** Session types allowed when planification is individual. */
export const INDIVIDUAL_SESSION_TYPE_VALUES: SessionType[] = ['individual_tutoring'];

/** In-person session type {@link Select} options. */
export const SESSION_IN_PERSON_TYPE_OPTIONS = [
  { value: 'homework_help', label: 'Aide aux devoirs' },
  { value: 'individual_tutoring', label: 'Tutorat individuel' },
  { value: 'workshop', label: 'Atelier' },
  {
    value: 'whole_class',
    label: 'Accompagnement en classe',
    tooltip: '1 compte rendu généré pour cette séance',
  },
  { value: 'group_tutoring', label: 'Tutorat de groupe' },
  { value: 'quick_help_desk', label: "Bureau d'aide rapide" },
];

/** In-person session format {@link Select} options. */
export const SESSION_IN_PERSON_FORMAT_OPTIONS = [{ value: 'in_person', label: 'Présentiel' }];

/** In-person session mode {@link Select} options (single vs recurring). */
export const SESSION_IN_PERSON_MODE_OPTIONS = [
  { value: 'single', label: 'Séance unique' },
  { value: 'recurring', label: 'Récurrente' },
];

/** In-person recurrence {@link Select} options (create form). */
export const SESSION_IN_PERSON_RECURRENCE_OPTIONS = [
  { value: 'daily', label: 'Quotidienne' },
  { value: 'weekly', label: 'Hebdomadaire' },
  { value: 'several_days_per_week', label: 'Plusieurs jours/semaine' },
  { value: 'monthly', label: 'Mensuelle' },
  { value: 'ab', label: 'Bi-mensuelle (semaines A/B)' },
];

/** Recurrence scope options when creating a session. */
export const SESSION_RECURRENCE_SCOPE_CREATE_OPTIONS = [
  { value: 'occurrence', label: 'Uniquement cette séance' },
  { value: 'following', label: 'Cette séance et les suivantes' },
] as const;

/** Recurrence scope options when editing a session. */
export const SESSION_RECURRENCE_SCOPE_EDIT_OPTIONS = [
  { value: 'occurrence', label: 'Cette séance uniquement' },
  { value: 'following', label: 'Cette séance et les suivantes' },
] as const;

/** Recurrence options for the list filter (not the form). */
export const SESSION_IN_PERSON_RECURRENCE_FILTER_OPTIONS = [
  { value: '', label: 'Toutes' },
  { value: 'none', label: SESSION_RECURRENCE_LABELS.none },
  { value: 'weekly', label: SESSION_RECURRENCE_LABELS.weekly },
  { value: 'ab', label: SESSION_RECURRENCE_LABELS.ab },
];

/** ISO weekdays (1 = Monday … 7 = Sunday) for `recurrenceRule.weekDays`. */
export const SESSION_WEEKDAY_LABELS: Record<number, string> = {
  1: 'Lundi',
  2: 'Mardi',
  3: 'Mercredi',
  4: 'Jeudi',
  5: 'Vendredi',
  6: 'Samedi',
  7: 'Dimanche',
};

/** Short weekday chips for recurrence week-day pickers. */
export const SESSION_WEEKDAY_SHORT_OPTIONS = [
  { day: 1, label: 'Lun' },
  { day: 2, label: 'Mar' },
  { day: 3, label: 'Mer' },
  { day: 4, label: 'Jeu' },
  { day: 5, label: 'Ven' },
  { day: 6, label: 'Sam' },
  { day: 7, label: 'Dim' },
] as const;

/** Role codes shown in the in-person animator filter. */
export const SESSION_IN_PERSON_ANIMATOR_FILTER_ROLE_CODES = [
  'coordinator',
  'manager',
  'recruiter',
  'admin',
] as const;

/** Empty-state copy when no in-person report exists yet. */
export const SESSION_IN_PERSON_REPORT_EMPTY_MESSAGE: Record<string, string> = {
  information_meeting_in_person: 'Aucun compte rendu disponible pour cette séance',
};

/** Create-flow card for an in-person session planification type. */
export interface SessionInPersonCreateTypeOption {
  id: PlanificationType;
  title: string;
  description: string;
  icon: LucideIcon;
  accentVar: string;
  tab: SessionInPersonTab;
}

/** Create-flow cards for in-person planification types. */
export const SESSION_IN_PERSON_CREATE_TYPE_OPTIONS: SessionInPersonCreateTypeOption[] = [
  {
    id: 'individual',
    title: 'Planification individuelle',
    description: 'Séance dédiée à un tutorat individuel.',
    icon: User,
    accentVar: 'var(--color-1-nuance-1)',
    tab: 'tutoring',
  },
  {
    id: 'collective',
    title: 'Planification collective',
    description:
      "Aide aux devoirs, atelier, tutorat, bureau d'aide rapide ou accompagnement en classe pour un groupe.",
    icon: Users,
    accentVar: 'var(--color-2-nuance-1)',
    tab: 'tutoring',
  },
  {
    id: 'information_meeting',
    title: "Réunion d'information",
    description:
      "Réunion d'information avec animateur(s) et jusqu'à 25 tuteurs participants en visio.",
    icon: Info,
    accentVar: 'var(--color-2-nuance-1)',
    tab: 'information-meeting',
  },
];

/** Create-type options visible for the allowed in-person tabs (all when unrestricted). */
export const getVisibleInPersonCreateTypeOptions = (
  allowedTabIds?: SessionInPersonTab[],
): SessionInPersonCreateTypeOption[] => {
  if (allowedTabIds && allowedTabIds.length > 0) {
    return SESSION_IN_PERSON_CREATE_TYPE_OPTIONS.filter((opt) => allowedTabIds.includes(opt.tab));
  }
  return SESSION_IN_PERSON_CREATE_TYPE_OPTIONS;
};

/** Organizer role group type when picking session organizers. */
export type SessionOrganizerGroupType = 'tutor' | 'volunteer';

/** Organizer role group shown when picking session organizers. */
export interface SessionOrganizerGroupOption {
  type: SessionOrganizerGroupType;
  label: string;
  description: string;
  icon: LucideIcon;
  accentColor: string;
}

/** Organizer role groups shown when picking session organizers. */
export const SESSION_ORGANIZER_GROUP_OPTIONS: SessionOrganizerGroupOption[] = [
  {
    type: 'tutor',
    get label() {
      return getRoleLabelPlural('tutor');
    },
    description: 'Bénévoles encadrant les séances pédagogiques',
    icon: GraduationCap,
    accentColor: 'var(--color-2-nuance-1)',
  },
  {
    type: 'volunteer',
    get label() {
      return `${getRoleLabelPlural('volunteer')} / ${getRoleLabelPlural('professor')}`;
    },
    description: 'Volontaires ou professeurs encadrant les séances',
    icon: Heart,
    accentColor: 'var(--color-3-nuance-1)',
  },
];

/** Badge config for auto-assignment sources. */
export const AUTO_ASSIGNMENT_SOURCE_LABELS: Record<
  'auto_binome_coeur' | 'auto_balanced',
  { label: string; icon: LucideIcon; color: string }
> = {
  auto_binome_coeur: {
    label: 'Binôme cœur',
    icon: Heart,
    color: 'var(--accent-pink, #e11d48)',
  },
  auto_balanced: {
    label: 'Équilibrage',
    icon: Scale,
    color: 'var(--color-2-nuance-1)',
  },
};

/**
 * Maps a session type key to its {@link SessionRemoteTab} category.
 * Derived from {@link SESSION_REMOTE_TYPE_FILTER_KEYS} order / tab routing.
 */
export const SESSION_TYPE_TO_TAB: Record<string, SessionRemoteTab> = {
  individual_tutoring: 'tutoring',
  information_meeting: 'information-meeting',
  welcome_meeting: 'welcome-meeting',
  workshop: 'field-event',
};

/** Session detail panel tabs. */
export type SessionDetailTab = 'details' | 'participants' | 'reports' | 'history';

/** Tabs on the session detail view. */
export const SESSION_DETAIL_TAB_CONFIGS: Array<{
  id: SessionDetailTab;
  label: string;
  icon: LucideIcon;
}> = [
  { id: 'details', label: 'Détails', icon: FileText },
  { id: 'participants', label: 'Participants', icon: Users },
  { id: 'reports', label: 'Retours', icon: Star },
  { id: 'history', label: 'Historique', icon: History },
];
